import React, { useEffect, createContext, useContext } from 'react';

const TrackingContext = createContext<{ sessionId: string } | null>(null);

export const sendPublicTelemetry = (endpoint: string, payload: Record<string, unknown>) => {
    const body = JSON.stringify(payload);
    if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
        return;
    }
    void fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'same-origin'
    }).catch(() => undefined);
};

export const TrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentPath, setCurrentPath] = React.useState(window.location.pathname);
    
    // We can also listen to popstate if the app uses history API for navigation
    useEffect(() => {
        const handleLocationChange = () => {
            setCurrentPath(window.location.pathname);
        };
        window.addEventListener('popstate', handleLocationChange);
        
        // Setup monkey-patch for pushState/replaceState
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;
        history.pushState = function (...args) {
            originalPushState.apply(this, args);
            handleLocationChange();
        };
        history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            handleLocationChange();
        };

        return () => {
            window.removeEventListener('popstate', handleLocationChange);
            history.pushState = originalPushState;
            history.replaceState = originalReplaceState;
        };
    }, []);

    const sessionId = React.useMemo(() => {
        let sid = sessionStorage.getItem('mobilbo_session_id');
        if (!sid) {
            sid = typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            sessionStorage.setItem('mobilbo_session_id', sid);
        }
        return sid;
    }, []);

    useEffect(() => {
        sendPublicTelemetry('/api/public/visit', {
            endpoint: currentPath + window.location.search,
            userAgent: navigator.userAgent,
            sessionId
        });
    }, [currentPath, sessionId]);

    useEffect(() => {
        const handleGlobalClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const clickable = target.closest('button, a, [role="button"]');
            
            if (clickable) {
                sendPublicTelemetry('/api/public/track', {
                    target: clickable.tagName.toLowerCase(),
                    label: clickable.textContent?.trim().slice(0, 50) || 'unlabeled',
                    path: currentPath,
                    sessionId
                });
            }
        };

        document.addEventListener('click', handleGlobalClick);
        return () => document.removeEventListener('click', handleGlobalClick);
    }, [currentPath, sessionId]);

    return (
        <TrackingContext.Provider value={{ sessionId }}>
            {children}
        </TrackingContext.Provider>
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTracking = () => useContext(TrackingContext);
